import { defineStore } from "pinia";
import Http from '../utils/http';
import Utils from '../utils/utils';
import GameApiService from '../api-services/GameApiService';
import Router from "../routes";
import { useRootStore } from "./root";
import { useToastsStore } from "./toasts";
import i18n from "../localizations";

export const useGameStore = defineStore("game", {
    state: () => ({
        templateId: 0,
        teamId: 0,
        currentRound: 0,
        totalRounds: 0,
        teamDisqualified: false,
        teamRoundEnded: false,
        roundEnded: false,

        remainingTime: null,
        remainingTimeByServer: null,
        remainingTimeByServerTimestamp: null,

        previousRoundResults: null,

        divisions: [],
        chats: [],
        employees: [],
        parameters: [],
        documents: [],
        playerParameters: [],
        skillParameters: [],
        storeParamsRequest: {
            active: false,
            final: false
        }
    }),

    actions: {
        // TODO: jednoduché "set" metódy sú zbytočné
        setTemplateId(templateId) {
            this.templateId = templateId;
        },

        setTeamId(teamId) {
            this.teamId = teamId
        },

        setCurrentRound(round) {
            this.currentRound = round;
        },

        setTotalRounds(rounds) {
            this.totalRounds = rounds;
        },

        setTeamDisqualified(disqualified) {
            this.teamDisqualified = disqualified;
        },

        setEndOfTheRoundStatus({ teamRoundEnded, roundEnded }) {
            this.teamRoundEnded = teamRoundEnded;
            this.roundEnded = roundEnded;
        },

        setRemainingTime(time) {
            this.remainingTime = time;
        },

        updateRemainingTime() {
            const { remainingTimeByServer, remainingTimeByServerTimeStamp } = this;

            if (remainingTimeByServerTimeStamp === null || remainingTimeByServer === null) {
                // ... cannot update remaining time without having required data
                return;
            } else {
                if (remainingTimeByServer <= 0) {
                    this.remainingTime = 0;
                } else {
                    const newValue = remainingTimeByServer - (performance.now() - remainingTimeByServerTimeStamp);
                    this.remainingTime = newValue <= 0 ? 0 :  Math.floor(newValue / 1000);
                }
            }
        },

        setDivisions(divisions) {
            this.divisions = [];

            divisions.forEach(division =>
                this.divisions.push({
                    id: division['id'],
                    key: division['key'],
                    playerId: division['player_id'],
                    userId: division['user_id'],
                    userFullName: division['user_full_name'],
                    estimatedCost: division['estimated_cost'],
                    controllable: division['controllable'],
                    active: false,
                    alert: false
                })
            );
        },

        setDivisionCosts(divisions) {
            if (this.divisions.length === 0) return;

            divisions.forEach((division) => {
                const foundDivision = this.divisions.find(d => d.id === division.id); 
                
                if (foundDivision) {
                    foundDivision.estimatedCost = division['estimated_cost'];
                } else {
                    console.error("Division not found!", division);
                }
            });
        },

        setParameters(parameters) {
            this.parameters = [];

            parameters.forEach(parameter => this.parameters.push({
                id: parameter['id'],
                parameterId: parameter['parameter_id'],
                key: parameter['key'],
                value: parameter['value'],
                unit: parameter['unit']
            }));
        },

        setDocuments(documents) {
            this.documents = [];

            documents.forEach(document => this.documents.push({
                id: document['id'],
                name: document['name'],
                type: document['type'],
                subtype: document['subtype'],
                content: document['content'],
                locale: document['locale']
            }));
        },

        setPlayerParameters(parameters) {
            this.playerParameters = [];

            parameters.forEach(parameter => this.playerParameters.push({
                id: parameter['id'],
                parameterId: parameter['parameter_id'],
                key: parameter['key'],
                value: parameter['value'],
                final: parameter['final'],
                options: parameter['options'],
                required: parameter['required']
            }));
        },

        setSkillParameters(parameters) {
            this.skillParameters = [];

            parameters.forEach(parameter => this.skillParameters.push({
                id: parameter['id'],
                employeeId: parameter['employee_id'],
                skillId: parameter['skill_id'],
                round: parameter['round']
            }));
        },

        setActiveDivision(divisionId) {
            this.divisions.forEach(division => division.active = division.id === divisionId);
        },

        setChats(chats) {
            // [TODO] Some attributes are not used
            this.chats = chats.map(chat => ({
                id: chat['id'],
                gameId: chat['game_id'],
                public: chat['public'],
                teamId: chat['team_id'],
                title: chat['title'],
                users: chat['users'],
                withLecturer: chat['with_lecturer'],
                color: chat['team_id'] !== null ? 'purple': chat['with_lecturer'] ? 'success' : 'primary',
                isShown: false,
                notSeenMessage: false,
                messages: chat.messages.map(message => ({
                    chatRoomId: message['chat_room_id'],
                    userId: message['user_id'],
                    userName: message['user_name'],
                    roleType: message['role_type'],
                    message: message['message'],
                    attachment: message['attachment'],
                    owner: message['owner'],
                    createdAt: message['created_at'],
                }))
            }))
        },

        addChatMessage({ newChatMessage, fromWebsocket }) {
            const chat = this.chats.find(chat => chat.id === newChatMessage["chat_room_id"]);

            if (chat) {
                if (fromWebsocket && !chat.isShown) {
                    chat.notSeenMessage = true;
                }

                chat.messages.push({
                    chatRoomId: newChatMessage['chat_room_id'],
                    userId: newChatMessage['user_id'],
                    userName: newChatMessage['user_name'],
                    roleType: newChatMessage['role_type'],
                    message: newChatMessage['message'],
                    attachment: newChatMessage['attachment'],
                    createdAt: newChatMessage['created_at'],
                });
            } else {
                console.error(`Chat s ID ${newChatMessage["chat_room_id"]} nebol nájdený!`);
            }
        },

        setEmployees(employees) {
            this.employees = [];

            employees.forEach(employee => this.employees.push({
                id: employee['id'],
                key: employee['key'],
                description: employee['description'],
                maxSkills: employee['max_skills'],
                skills: employee['skills'] ? employee['skills'] : []
            }));
        },

        assignEmployeeSkills(employee) {
            const foundEmployee = this.employees.find(e => e.id === employee.id);

            if (foundEmployee) {
                foundEmployee.skills = employee.skills;
            }
        },
        
        checkGame(gameId) {
            return new Promise((resolve, reject) => {
                Http.get(`/api/games/${gameId}/check-game`)
                    .then(response => resolve(response))
                    .catch(error => reject(error));
            })
        },

        async loadInitState(gameId) {
            const rootStore = useRootStore();
            
            await Http.get(`/api/games/${gameId}/init-state`)
                .then(response => {
                    const initState = response.data;
                    const gameState = initState['game_state']; 
                    const toastsStore = useToastsStore();
                    
                    if (gameState === 'finished') {
                        toastsStore.warning(i18n.t("toastErrors.game.game_finished"));
                        Router.push({ name: 'dashboard' });
                        return;
                    } else if (gameState === 'canceled') {
                        toastsStore.warning(i18n.t("toastErrors.game.game_canceled"));
                        Router.push({ name: 'dashboard' });
                        return;
                    } else if (gameState !== 'started') {
                        Router.push({ name: 'dashboard' });
                        return;
                    }
                    
                    const isCurrentUserPlayer = rootStore.isPlayer;
                    const playerDivision = Utils.findDivisionOfPlayer(initState['user'], initState['divisions']);

                    this.setTemplateId(initState['template_id']);
                    this.setTeamId(initState['user']['team_id']);
                    this.setCurrentRound(initState['current_round']);
                    this.setTotalRounds(initState['total_rounds']);
                    this.setDocuments(initState['documents']);
                    this.setEmployees(initState['employees']);
                    this.setParameters(initState['parameters']);
                    this.setTeamDisqualified(initState['team_disqualified']);
                    this.setEndOfTheRoundStatus({
                        teamRoundEnded: initState['team_round_ended'],
                        roundEnded: initState['round_ended']
                    });

                    this.remainingTimeByServer = initState['remaining_time'] * 1000;
                    this.remainingTimeByServerTimeStamp = performance.now();

                    if (isCurrentUserPlayer)
                    {
                        this.setDivisions(initState['divisions']);
                        this.setPlayerParameters(initState['player_parameters']);
                        this.setSkillParameters(initState['skill_parameters']);
                        this.setActiveDivision(playerDivision.id);

                        this.loadCurrentState(gameId);
                        
                        if (initState['current_round'] > 1) {
                            this.loadPreviousRoundResults(gameId);
                        }
                    }

                    initState['skill_parameters'].forEach(skillParameter => {
                        const foundEmployee = this.employees.find(employee => employee.id === skillParameter['employee_id']);

                        if (foundEmployee) {
                            foundEmployee.skills.push({
                                id: skillParameter['skill_id'],
                                key: skillParameter['skill_key'],
                                selected: false
                            });
                        }
                    });
                })
                .catch(error => console.error(error));
        },

        async loadPreviousRoundResults(gameId) {
            try {
                const results = await GameApiService.getPreviousRoundResults(gameId);
                this.previousRoundResults = results;
            } catch (error) {
                // TODO: Handle errors
            }
        },

        loadSkills(templateId) {
            return new Promise((resolve, reject) => {
                Http.get(`/api/templates/${templateId}/skills`)
                    .then(response => resolve(response.data))
                    .catch(error => reject(error));
            });
        },

        async loadChats(gameId) {
            const { data: chats } = await Http.get(`/api/games/${gameId}/chats`);
            this.setChats(chats);
        },

        loadCurrentState(gameId) {
            Http.get(`/api/games/${gameId}/load-state`)
                .then(response => {
                    const currentState = response.data;

                    const hrDivision = this.divisions.find(division => division.key === 'hr_division');
                    const employees = this.employees.map(employee => ({
                        id: employee.id,
                        skills: []
                    }));

                    this.setDivisionCosts(currentState['divisions']);
                    this.setPlayerParameters(currentState['player_parameters']);
                    this.setSkillParameters(currentState['skill_parameters']);
                    this.setTeamDisqualified(currentState['team_disqualified']);
                    this.setEndOfTheRoundStatus({
                        teamRoundEnded: currentState['team_round_ended'],
                        roundEnded: currentState['round_ended']
                    });

                    if (hrDivision && !hrDivision.controllable) {
                        currentState['skill_parameters'].forEach(skillParameter => {
                            const foundEmployee = employees.find(employee => employee.id === skillParameter['employee_id']);

                            if (foundEmployee) {
                                foundEmployee.skills.push({
                                    id: skillParameter['skill_id'],
                                    key: skillParameter['skill_key'],
                                    selected: false
                                });

                                this.assignEmployeeSkills(foundEmployee);
                            }
                        });
                    }

                })
                .catch(error => {
                    console.error(error);
                });
        },

        async storeMessage(formData) {
            const chatRoomId = formData.get('chat_room_id');

            if (formData.get('chat_room_id'))
                formData.delete('chat_room_id');

            const { data } = await Http.post(`/api/chats/${chatRoomId}/store-message`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            
            this.addChatMessage({ newChatMessage: data });
        },

        uploadChatAttachment(payload) {
            const chatRoomId = payload.get('chat_room_id');

            if (payload.get('chat_room_id'))
                payload.delete('chat_room_id');

            return new Promise((resolve, reject) => {
                Http.post(`/api/chats/${chatRoomId}/upload-attachment`, payload, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })
                    .then(response => resolve(response.data))
                    .catch(error => reject(error));
            });
        },

        storeParameters(payload) {
            const gameId = payload.game_id;
            delete payload.game_id;

            this.storeParamsRequest.active = true;
            this.storeParamsRequest.final = payload.final;
            
            const rootStore = useRootStore();

            Http.post(`/api/games/${gameId}/store-parameters`, payload)
                .then(_ => rootStore.clearErrors())
                .catch(error => Utils.resolveValidationErrors(error))
                .finally(() => {
                    this.storeParamsRequest.active = false;
                })
        },

        evaluateRound(gameId) {
            return new Promise((resolve, reject) => {
                Http.post(`/api/games/${gameId}/evaluate-round`)
                    .then(response => resolve(response))
                    .catch(error => reject(error));
            });
        },

        loadRoundResults(gameId) {
            return new Promise((resolve, reject) => {
                Http.get(`/api/games/${gameId}/round-results`)
                    .then(response => resolve(response))
                    .catch(error => reject(error));
            });
        },

        clearState() {
            this.$reset();
        },
    }
})